import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Play, Save, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import adminApi from "@/lib/adminApi";

export const PrerollTab = ({ onRefresh }) => {
  const [prerolls, setPrerolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPreroll, setEditingPreroll] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    video_url: "",
    platform: "youtube",
    skip_after_seconds: 5,
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchPrerolls();
  }, []);

  const fetchPrerolls = async () => {
    try {
      const res = await adminApi.get("/prerolls");
      setPrerolls(res.data);
    } catch (error) {
      console.error("Error fetching prerolls:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.video_url.trim()) {
      toast.error("La URL del video es requerida");
      return;
    }

    setSaving(true);
    try {
      if (editingPreroll) {
        await adminApi.put(`/prerolls/${editingPreroll.id}`, formData);
        toast.success("Preroll actualizado");
      } else {
        await adminApi.post("/prerolls", formData);
        toast.success("Preroll creado");
      }
      resetForm();
      fetchPrerolls();
      onRefresh?.();
    } catch (error) {
      toast.error("Error al guardar el preroll");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (preroll) => {
    setEditingPreroll(preroll);
    setFormData({
      title: preroll.title || "",
      video_url: preroll.video_url || "",
      platform: preroll.platform || "youtube",
      skip_after_seconds: preroll.skip_after_seconds || 5,
      is_active: preroll.is_active ?? true,
      order: preroll.order || 0
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este preroll?")) return;
    
    try {
      await adminApi.delete(`/prerolls/${id}`);
      toast.success("Preroll eliminado");
      fetchPrerolls();
      onRefresh?.();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleActive = async (preroll) => {
    try {
      await adminApi.put(`/prerolls/${preroll.id}`, {
        is_active: !preroll.is_active
      });
      toast.success(preroll.is_active ? "Preroll desactivado" : "Preroll activado");
      fetchPrerolls();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const resetForm = () => {
    setEditingPreroll(null);
    setFormData({
      title: "",
      video_url: "",
      platform: "youtube",
      skip_after_seconds: 5,
      is_active: true,
      order: 0
    });
  };

  // Get YouTube/Vimeo thumbnail
  const getThumbnail = (url, platform) => {
    if (platform === "youtube") {
      const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
      if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Preroll Ads</h2>
          <p className="text-sm text-gray-500">Configura los anuncios que aparecen antes de cada video</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título (interno)
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ej: Promo Tienda Navidad"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plataforma
            </label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL del Video *
          </label>
          <input
            type="text"
            value={formData.video_url}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Segundos antes de poder saltar
            </label>
            <input
              type="number"
              min="0"
              max="30"
              value={formData.skip_after_seconds}
              onChange={(e) => setFormData({ ...formData, skip_after_seconds: parseInt(e.target.value) || 5 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Orden (prioridad)
            </label>
            <input
              type="number"
              min="0"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Menor número = mayor prioridad</p>
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              <span className="text-sm font-medium text-gray-700">Activo</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="bg-pink-600 hover:bg-pink-700">
            <Save size={18} className="mr-2" />
            {editingPreroll ? "Actualizar" : "Crear"} Preroll
          </Button>
          {editingPreroll && (
            <Button type="button" variant="outline" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>

      {/* List */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-800">Prerolls configurados</h3>
        
        {prerolls.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
            <Play size={48} className="mx-auto mb-3 opacity-30" />
            <p>No hay prerolls configurados</p>
            <p className="text-sm">Crea uno arriba para empezar</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {prerolls.map((preroll) => (
              <div 
                key={preroll.id}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  preroll.is_active 
                    ? "bg-white border-green-200" 
                    : "bg-gray-100 border-gray-200 opacity-60"
                }`}
              >
                {/* Thumbnail */}
                <div className="w-32 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  {getThumbnail(preroll.video_url, preroll.platform) ? (
                    <img 
                      src={getThumbnail(preroll.video_url, preroll.platform)} 
                      alt={preroll.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900 truncate">
                      {preroll.title || "Sin título"}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      preroll.is_active 
                        ? "bg-green-100 text-green-700" 
                        : "bg-gray-200 text-gray-600"
                    }`}>
                      {preroll.is_active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{preroll.video_url}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                    <span>Plataforma: {preroll.platform}</span>
                    <span>Skip: {preroll.skip_after_seconds}s</span>
                    <span>Orden: {preroll.order}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(preroll)}
                    className={preroll.is_active ? "text-green-600" : "text-gray-400"}
                  >
                    {preroll.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(preroll)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(preroll.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <strong>¿Cómo funciona?</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>El preroll activo con menor orden se muestra antes de cada video</li>
          <li>El usuario debe esperar X segundos antes de poder saltar</li>
          <li>Después de saltar o terminar el anuncio, el video principal inicia automáticamente</li>
          <li>Si no hay prerolls activos, el video principal se muestra directamente</li>
        </ul>
      </div>
    </div>
  );
};
